import React, { useState, useEffect, useMemo } from 'react';

import { X, Save, Clock, Settings, Activity, Info } from 'lucide-react';

import baseapi from '../../../services/baseapi';

import { showError, showSuccess } from '../../../utils/toast';

import { CustomDropdown } from '../../../components/CustomDropdown ';

import MultiSelectApiDropdown from '../../../components/MultiSelectApiDropdown';

import { useClientLevel } from '../../../hooks/useClientLevel';
import SingleSelectLocalSearchDropdown from '../../../components/SingleSelectLocalSearchDropdown';





interface AddScheduleModalProps {

    isOpen: boolean;

    onClose: () => void;

    onSuccess: () => void;

    editData?: any;

}



// Generate options for Hours (0-23) and Minutes (0-59)

const hourOptions = [

    { label: 'Every Hour (*)', value: '' },

    ...Array.from({ length: 24 }, (_, i) => ({

        label: i.toString().padStart(2, '0'),

        value: i.toString()

    }))

];



const minuteOptions = [

    { label: 'Every Minute (*)', value: '' },

    ...Array.from({ length: 60 }, (_, i) => ({

        label: i.toString().padStart(2, '0'),

        value: i.toString()

    }))

];

const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isCustomReportType = (type?: string) => {
    if (!type) return false;
    const lower = String(type).toLowerCase();
    return (
        lower === 'custom_report_generation' ||
        lower === 'custom-report-generation' ||
        lower === 'custom_report' ||
        lower === 'custom-report' ||
        lower === 'custom_reports' ||
        lower === 'custom-reports' ||
        (lower.includes('custom') && lower.includes('report'))
    );
};



export default function AddScheduleModal({ isOpen, onClose, onSuccess, editData }: AddScheduleModalProps) {

    const [loading, setLoading] = useState(false);



    const [name, setName] = useState('');

    const [isEnabled, setIsEnabled] = useState(true);

    const [taskType, setTaskType] = useState<any>(null);



    const [format, setFormat] = useState('pdf');

    const [datePeriod, setDatePeriod] = useState('last_24_hours');

    const [reportKeys, setReportKeys] = useState<any[]>([]);

    const [customReports, setCustomReports] = useState<any[]>([]);

    const [customReportFormat, setCustomReportFormat] = useState('excel');

    const [recipients, setRecipients] = useState('');

    const [recipientInput, setRecipientInput] = useState('');

    const [agents, setAgents] = useState<any[]>([]);

    const [clients, setClients] = useState<any[]>([]);



    const [schedulerType, setSchedulerType] = useState('daily');

    // Empty strings by default — maps to wildcard (*)

    const [hours, setHours] = useState('');

    const [minutes, setMinutes] = useState('');



    const [daysOfWeek, setDaysOfWeek] = useState<string[]>([]);

    const [selectedDays, setSelectedDays] = useState<number[]>([]);

    const [selectedMonths, setSelectedMonths] = useState<string[]>([]);

    const [oneTimeDate, setOneTimeDate] = useState('');

    const [timezone, setTimezone] = useState('UTC');
    const [selectedScripts, setSelectedScripts] = useState<any[]>([]);



    const level = useClientLevel();
    const isClient = Boolean(level?.id && level.name !== 'global');
    const currentLevelName = isClient ? 'client' : 'global';

    const [taskTypesList, setTaskTypesList] = useState<any[]>([]);

    useEffect(() => {
        if (isOpen) {
            baseapi.get('/scheduler/task-types').then(response => {
                if (response.data.status && Array.isArray(response.data.data)) {
                    const list = response.data.data;
                    setTaskTypesList(list);
                    if (editData && editData.task_type) {
                        const matched = list.find((tt: any) => tt.type === editData.task_type);
                        if (matched) {
                            setTaskType(matched);
                        }
                    }
                }
            }).catch(err => console.error("Failed to fetch task types", err));
        } else {
            setTaskTypesList([]);
        }
    }, [isOpen, editData]);

    const filteredTaskTypes = useMemo(() => {
        return (taskTypesList || []).filter((item: any) => {
            if (!item) return false;
            if (!item.levels || !Array.isArray(item.levels) || item.levels.length === 0) {
                return true;
            }
            const normalizedLevels = item.levels.map((l: any) => String(l).toLowerCase().trim());
            return normalizedLevels.includes(currentLevelName);
        });
    }, [taskTypesList, currentLevelName]);

    const taskTypeDropdownOptions = useMemo(() => {
        return filteredTaskTypes.map((item: any) => ({
            id: item.type,
            label: item.label || item.type,
            value: item.type,
            ...item
        }));
    }, [filteredTaskTypes]);

    useEffect(() => {
        if (taskType && taskType.levels && Array.isArray(taskType.levels)) {
            const normalizedLevels = taskType.levels.map((l: any) => String(l).toLowerCase().trim());
            if (!normalizedLevels.includes(currentLevelName)) {
                setTaskType(null);
                setAgents([]);
            }
        }
    }, [currentLevelName, taskType]);

    const getAgentsAdditionalQueries = () => {
        const queryObj: Record<string, any> = {};

        if (level.id) {
            queryObj.client_id = level.id;
        } else if (clients.length > 0) {
            queryObj.client_id = clients.map((c: any) => parseInt(c.id, 10));
        }

        const currentTaskTypeType = taskType?.type || (typeof taskType === 'string' ? taskType : (taskType?.value || null));
        const matchedTaskType = taskType?.allowed_agent_types
            ? taskType
            : taskTypesList.find((tt: any) => tt.type === currentTaskTypeType);

        const allowedAgentTypes = matchedTaskType?.allowed_agent_types;

        if (allowedAgentTypes) {
            if (Array.isArray(allowedAgentTypes)) {
                const normalized = allowedAgentTypes.map((t: any) => typeof t === 'string' ? t.toLowerCase() : t);
                if (normalized.length === 1) {
                    queryObj.agent_type = normalized[0];
                } else if (normalized.length > 1) {
                    queryObj.agent_type = normalized;
                }
            } else if (typeof allowedAgentTypes === 'string') {
                queryObj.agent_type = allowedAgentTypes.toLowerCase();
            }
        }

        // Send platform: windows in query for AD scan task type
        const taskTypeStr = (currentTaskTypeType || '').toLowerCase();
        if (
            taskTypeStr === 'ad_scan' ||
            taskTypeStr === 'ad-scan' ||
            taskTypeStr === 'active_directory' ||
            taskTypeStr.includes('ad_scan') ||
            taskTypeStr.includes('active_directory')
        ) {
            queryObj.platform = 'windows';
        }

        return queryObj;
    };



    useEffect(() => {

        if (isOpen && editData) {

            setName(editData.name || '');

            setIsEnabled(editData.is_enabled ?? true);

            setTaskType({ type: editData.task_type, label: editData.task_type.replace(/_/g, ' ') });



            if (editData.scheduler_data) {

                const sData = typeof editData.scheduler_data === 'string' ? JSON.parse(editData.scheduler_data) : editData.scheduler_data;

                setSchedulerType(sData.scheduler_type || 'daily');



                // Map existing values to dropdown, converting '*' back to empty string

                const h = sData.hours?.toString();

                const m = sData.minutes?.toString();

                setHours(h === '*' ? '' : (h ?? ''));

                setMinutes(m === '*' ? '' : (m ?? ''));



                if (sData.days_of_week) setDaysOfWeek(sData.days_of_week);

                else setDaysOfWeek([]);

                if (sData.days && Array.isArray(sData.days)) setSelectedDays(sData.days);

                else setSelectedDays([]);

                if (sData.months && Array.isArray(sData.months)) setSelectedMonths(sData.months);

                else setSelectedMonths([]);

                if (sData.date) setOneTimeDate(sData.date);

                else setOneTimeDate('');

                if (sData.timezone) setTimezone(sData.timezone);

            }



            if (editData.data) {

                const tData = typeof editData.data === 'string' ? JSON.parse(editData.data) : editData.data;

                setFormat(tData.format || 'pdf');

                setDatePeriod(tData.date_period || 'last_24_hours');

                setRecipients(tData.recipients ? tData.recipients.join(', ') : '');

                if (tData.agent_id) {
                    const rawAgentIds = Array.isArray(tData.agent_id) ? tData.agent_id : [tData.agent_id];
                    const agentIds = rawAgentIds.filter((id: any) => id !== null && id !== undefined);
                    let mappedAgents = agentIds.map((id: any) => ({ id, value: id, name: `Agent #${id}`, label: `Agent #${id}` }));
                    if (editData.agent_info) {
                        try {
                            const agentInfo = typeof editData.agent_info === 'string' ? JSON.parse(editData.agent_info) : editData.agent_info;
                            const agentInfoArr = Array.isArray(agentInfo) ? agentInfo : [agentInfo];
                            mappedAgents = agentInfoArr.filter((a: any) => a && a.id !== null).map((a: any) => ({
                                id: a.id,
                                value: a.id,
                                name: a.agent_name || a.name || a.label || `Agent #${a.id}`,
                                label: a.agent_name || a.name || a.label || `Agent #${a.id}`
                            }));
                        } catch (e) {
                            console.error("Failed to parse agent_info", e);
                        }
                    }
                    setAgents(mappedAgents);
                }

                if (tData.report_keys) {

                    const rKeys = tData.report_keys.map((key: string) => ({

                        report_key: key,

                        title: key.charAt(0).toUpperCase() + key.slice(1)

                    }));

                    setReportKeys(rKeys);

                }

                if (tData.custom_report_ids) {
                    const rawCustomReportIds = Array.isArray(tData.custom_report_ids) ? tData.custom_report_ids : [tData.custom_report_ids];
                    const crIds = rawCustomReportIds.filter((cid: any) => cid !== null && cid !== undefined);
                    setCustomReportFormat(tData.format || 'excel');

                    let mappedCRs = crIds.map((cid: any) => ({
                        id: cid,
                        value: cid,
                        report_name: `Report #${cid}`,
                        name: `Report #${cid}`
                    }));

                    if (editData.custom_report_info || editData.report_info) {
                        try {
                            const crInfo = typeof (editData.custom_report_info || editData.report_info) === 'string'
                                ? JSON.parse(editData.custom_report_info || editData.report_info)
                                : (editData.custom_report_info || editData.report_info);
                            const crInfoArr = Array.isArray(crInfo) ? crInfo : [crInfo];
                            mappedCRs = crInfoArr.filter((c: any) => c && c.id !== null).map((c: any) => ({
                                id: c.id,
                                value: c.id,
                                report_name: c.report_name || c.name || `Report #${c.id}`,
                                name: c.report_name || c.name || `Report #${c.id}`
                            }));
                        } catch (e) {
                            console.error("Failed to parse custom_report_info", e);
                        }
                    } else {
                        const queryObj = level.id ? { client_id: level.id } : {};
                        baseapi.get('custom_reports', { params: { query: JSON.stringify(queryObj) } }).then(res => {
                            const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
                            const updatedCRs = crIds.map((cid: any) => {
                                const found = list.find((item: any) => item.id === cid);
                                return {
                                    id: cid,
                                    value: cid,
                                    report_name: found ? (found.report_name || found.name) : `Report #${cid}`,
                                    name: found ? (found.report_name || found.name) : `Report #${cid}`
                                };
                            });
                            setCustomReports(updatedCRs);
                        }).catch(err => console.error("Failed to fetch custom reports for edit", err));
                    }
                    setCustomReports(mappedCRs);
                }

            }

            if (editData.task_type === 'custom-script-execution') {
                const tData = typeof editData.data === 'string' ? JSON.parse(editData.data) : editData.data;
                if (tData && tData.script_ids) {
                    const scriptIds = tData.script_ids;
                    let mappedScripts = scriptIds.map((id: any) => ({ id, value: id, name: `Script #${id}`, label: `Script #${id}` }));

                    if (editData.script_info) {
                        try {
                            const scriptInfo = typeof editData.script_info === 'string' ? JSON.parse(editData.script_info) : editData.script_info;
                            const scriptInfoArr = Array.isArray(scriptInfo) ? scriptInfo : [scriptInfo];
                            mappedScripts = scriptInfoArr.filter((s: any) => s && s.id !== null).map((s: any) => ({
                                id: s.id,
                                value: s.id,
                                name: s.name || `Script #${s.id}`,
                                label: s.name || `Script #${s.id}`
                            }));
                        } catch (e) {
                            console.error("Failed to parse script_info", e);
                        }
                    } else {
                        // Fallback: fetch scripts to get names
                        const params = level.id ? { query: JSON.stringify({ client_id: level.id }) } : {};
                        baseapi.get('/custom_scripts', { params }).then(response => {
                            if (response.data.status) {
                                const allScripts = response.data.data || [];
                                const updatedScripts = scriptIds.map((id: any) => {
                                    const found = allScripts.find((s: any) => s.id === id);
                                    return {
                                        id,
                                        value: id,
                                        name: found ? found.name : `Script #${id}`,
                                        label: found ? found.name : `Script #${id}`
                                    };
                                });
                                setSelectedScripts(updatedScripts);
                            }
                        }).catch(err => console.error("Failed to fetch scripts for names", err));
                    }
                    setSelectedScripts(mappedScripts);
                }
            }

            if (editData.client_names) {
                try {
                    const parsedClients = typeof editData.client_names === 'string' ? JSON.parse(editData.client_names) : editData.client_names;
                    setClients(parsedClients.map((c: any) => ({ id: c.id, name: c.name || `Client #${c.id}` })));
                } catch (e) {
                    if (editData.client_id && Array.isArray(editData.client_id)) {
                        setClients(editData.client_id.map((c: any) => typeof c === 'object' ? { id: c.id, name: c.name } : { id: c, name: `Client #${c}` }));
                    }
                }
            } else if (editData.client_id && Array.isArray(editData.client_id)) {
                setClients(
                    editData.client_id.map((c: any) =>
                        typeof c === 'object' ? { id: c.id, name: c.name } : { id: c, name: `Client #${c}` }
                    )
                );
            }

        } else if (isOpen && !editData) {

            // Full reset — nothing predefined

            setName('');

            setIsEnabled(true);

            setTaskType(null);

            setSchedulerType('daily');

            setHours('');

            setMinutes('');

            setFormat('pdf');

            setDatePeriod('last_24_hours');

            setReportKeys([]);

            setCustomReports([]);

            setCustomReportFormat('excel');

            setRecipients('');

            setAgents([]);

            setClients([]);

            setDaysOfWeek([]);

            setSelectedDays([]);

            setSelectedMonths([]);

            setOneTimeDate('');

            setTimezone('UTC');

            setSelectedScripts([]);

        }

    }, [isOpen, editData]);



    if (!isOpen) return null;



    const getTaskTypeValue = (t: any): string => {
        if (!t) return '';
        if (typeof t === 'string') return t;
        if (t.type) return t.type;
        if (t.value) return t.value;
        if (t.id) return String(t.id);
        return '';
    };
    const currentTaskTypeType = getTaskTypeValue(taskType);
    const isCustomReport = isCustomReportType(currentTaskTypeType);
    const matchedTaskType = taskType?.destination
        ? taskType
        : taskTypesList.find((tt: any) => tt.type === currentTaskTypeType);
    const isAgentDestination = matchedTaskType?.destination === 'agent';
    const isLocalDestination = matchedTaskType?.destination === 'local' || isCustomReport;
    const showTimezone = Boolean(taskType && (matchedTaskType || isCustomReport) && !isAgentDestination);

    const baseFrequencyOptions = [
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Quarterly', value: 'quarterly' },
        { label: 'Yearly', value: 'yearly' }
    ];

    if (isLocalDestination) {
        baseFrequencyOptions.push({ label: 'One-time', value: 'one_time' });
    }

    const frequencyOptions = baseFrequencyOptions;

    const availableHourOptions = hourOptions.filter(o => o.value !== '');
    const availableMinuteOptions = minuteOptions.filter(o => o.value !== '');

    // Build the hours/minutes value with wildcard logic

    const buildTimeFields = () => {
        if (schedulerType === 'every_minute') {
            return { hours: '*', minutes: '*' };
        }
        if (schedulerType === 'every_hour' || schedulerType === 'hourly') {
            return { hours: '*', minutes: minutes.trim() || '0' };
        }

        const h = hours.trim();

        const m = minutes.trim();



        if (h && m) return { hours: h, minutes: m };

        if (h && !m) return { hours: h, minutes: '*' };

        if (!h && m) return { hours: '*', minutes: m };

        return { hours: '*', minutes: '*' };

    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            showError("Please enter Schedule Name");
            return;
        }

        if (!taskType) {
            showError("Please select Scheduler Type");
            return;
        }

        if (!level.id && clients.length === 0) {
            showError("Please select at least one Client");
            return;
        }

        const isCustomScript = taskType.type === 'custom-script-execution';
        const isCustomReport = isCustomReportType(taskType.type);

        if (isCustomReport) {
            if (customReports.length === 0) {
                showError("Please select at least one Custom Report");
                return;
            }

            let emailList = recipients.split(',').map(r => r.trim()).filter(r => r);
            if (recipientInput.trim()) {
                const unadded = recipientInput.trim().replace(',', '');
                if (!isValidEmail(unadded)) {
                    showError(`Invalid recipient email: ${unadded}`);
                    return;
                }
                if (!emailList.includes(unadded)) {
                    emailList.push(unadded);
                    setRecipients(emailList.join(', '));
                    setRecipientInput('');
                }
            }

            if (emailList.length === 0) {
                showError("Please enter at least one Recipient email");
                return;
            }

            const invalidEmails = emailList.filter(e => !isValidEmail(e));
            if (invalidEmails.length > 0) {
                showError(`Invalid recipient email(s): ${invalidEmails.join(', ')}`);
                return;
            }
        } else if (taskType.type === 'report_generation') {
            if (reportKeys.length === 0) {
                showError("Please select at least one Report");
                return;
            }

            let emailList = recipients.split(',').map(r => r.trim()).filter(r => r);
            if (recipientInput.trim()) {
                const unadded = recipientInput.trim().replace(',', '');
                if (!isValidEmail(unadded)) {
                    showError(`Invalid recipient email: ${unadded}`);
                    return;
                }
                if (!emailList.includes(unadded)) {
                    emailList.push(unadded);
                    setRecipients(emailList.join(', '));
                    setRecipientInput('');
                }
            }

            if (emailList.length === 0) {
                showError("Please enter at least one Recipient email");
                return;
            }

            const invalidEmails = emailList.filter(e => !isValidEmail(e));
            if (invalidEmails.length > 0) {
                showError(`Invalid recipient email(s): ${invalidEmails.join(', ')}`);
                return;
            }
        } else if (isCustomScript) {
            if (selectedScripts.length === 0) {
                showError("Please select at least one Script");
                return;
            }
            if (agents.length === 0) {
                showError("Please select at least one Agent");
                return;
            }
        } else {
            if (agents.length === 0) {
                showError("Please select at least one Agent");
                return;
            }
        }

        if (!schedulerType) {
            showError("Please select Frequency");
            return;
        }


        if (schedulerType !== 'every_minute' && schedulerType !== 'every_hour') {
            if (hours === '' || hours === undefined || hours === null) {
                showError("Please select Hour");
                return;
            }
            if (minutes === '' || minutes === undefined || minutes === null) {
                showError("Please select Minute");
                return;
            }
        }

        if (showTimezone && !timezone) {
            showError("Please select Timezone");
            return;
        }



        if (schedulerType === 'weekly' && daysOfWeek.length === 0) {
            showError("Please select at least one day of the week");
            return;
        }

        if ((schedulerType === 'monthly' || schedulerType === 'quarterly' || schedulerType === 'yearly') && selectedDays.length === 0) {
            showError("Please select at least one day of the month");
            return;
        }

        if (schedulerType === 'yearly' && selectedMonths.length === 0) {
            showError("Please select at least one month");
            return;
        }

        if (schedulerType === 'one_time' && !oneTimeDate) {
            showError("Please select Date for one-time schedule");
            return;
        }

        setLoading(true);



        try {

            const { hours: finalHours, minutes: finalMinutes } = buildTimeFields();



            let scheduler_data: any = {

                scheduler_type: schedulerType,

                hours: finalHours,

                minutes: finalMinutes,

                timezone: timezone || 'UTC'

            };



            if (schedulerType === 'weekly') scheduler_data.days_of_week = daysOfWeek;

            if (schedulerType === 'monthly' || schedulerType === 'quarterly') {

                scheduler_data.days = selectedDays;

            }

            if (schedulerType === 'yearly') {

                scheduler_data.months = selectedMonths;

                scheduler_data.days = selectedDays;

            }

            if (schedulerType === 'one_time') scheduler_data.date = oneTimeDate;



            let data: any = {};

            if (isCustomReport) {

                data.custom_report_ids = customReports.map((r: any) => parseInt(r.id || r.value, 10));

                data.format = customReportFormat;

                data.recipients = recipients.split(',').map(r => r.trim()).filter(r => r);

            } else if (taskType.type === 'report_generation') {

                data.format = format;

                data.date_period = datePeriod;

                data.report_keys = reportKeys.map((r: any) => r.report_key);

                data.recipients = recipients.split(',').map(r => r.trim()).filter(r => r);

            } else if (taskType.type === 'custom-script-execution') {
                data.script_ids = selectedScripts.map(s => parseInt(s.id || s.value, 10));
                data.agent_id = agents.map((a: any) => parseInt(a.value || a.id, 10));
            } else {

                data.agent_id = agents.map((a: any) => parseInt(a.value || a.id, 10));

                data.scan_type = "full";

            }



            const payload: any = {

                name,

                task_type: taskType.type,

                is_enabled: isEnabled,

                scheduler_data,

                data

            };



            if (level.id) {

                payload.client_id = level.id;

                payload.is_global = false;

            } else {

                payload.is_global = true;

                if (clients.length > 0) {

                    payload.client_id = clients.map((c: any) => parseInt(c.id, 10));

                }

            }




            const endpoint = editData ? `/scheduler/${editData.id}` : "/scheduler";

            const method = editData ? baseapi.put : baseapi.post;



            const response = await method(endpoint, payload);



            if (response.data.status) {

                showSuccess(`Schedule ${editData ? 'updated' : 'added'} successfully`);

                onSuccess();

                onClose();

            } else {

                showError(response.data.message || "Failed to save schedule");

            }

        } catch (error: any) {

            showError(error.message || "An error occurred");

        } finally {

            setLoading(false);

        }

    };



    const daysList = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    const monthsList = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

    const monthDaysList = Array.from({ length: 31 }, (_, i) => i + 1);

    const timezoneList = Intl.supportedValuesOf('timeZone').map(tz => ({ label: tz, value: tz }));





    const toggleMonthDay = (day: number) => {

        setSelectedDays(prev =>

            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b)

        );

    };



    const toggleMonth = (month: string) => {

        setSelectedMonths(prev =>

            prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]

        );

    };



    return (

        <div className="fixed inset-0 z-55 flex items-center justify-center p-4">

            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />



            <div className="relative bg-gray-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">

                {/* Header */}

                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-800/50">

                    <h2 className="text-lg font-bold text-white tracking-wide">

                        {editData ? 'Edit Schedule' : 'Add New Schedule'}

                    </h2>

                    <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors">

                        <X size={18} />

                    </button>

                </div>



                {/* Form Body */}

                <div className="flex-1 overflow-y-auto p-6 custom-scroll">

                    <div className="space-y-8">



                        {/* 1. Basic Details */}

                        <div className="space-y-4">

                            <h3 className="text-sm font-bold text-indigo-400 uppercase tracking-widest border-b border-slate-800 pb-2 flex items-center gap-2">

                                <Settings size={14} /> Basic Settings

                            </h3>

                            <div className="grid grid-cols-2 gap-4">

                                <div className="space-y-1.5 col-span-2">

                                    <label className="text-xs font-semibold text-slate-300">Schedule Name <span className="text-red-400">*</span></label>

                                    <input

                                        type="text"

                                        required

                                        value={name}

                                        onChange={(e) => setName(e.target.value)}

                                        placeholder="e.g. Daily Asset Report"

                                        className="w-full h-11 bg-gray-900 border border-slate-700/50 rounded-lg px-3 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all"

                                    />

                                </div>

                                <div className="col-span-1">

                                    <SingleSelectLocalSearchDropdown
                                        label="Scheduler Type"
                                        required
                                        value={taskType?.type || (typeof taskType === 'string' ? taskType : '')}
                                        onChange={(val) => {
                                            const found = taskTypesList.find((tt: any) => tt.type === val) || (val ? { type: val, label: String(val) } : null);
                                            setTaskType(found);
                                            setAgents([]);
                                        }}
                                        options={taskTypeDropdownOptions}
                                        icon={Activity}
                                        placeholder="Select Scheduler Type"
                                        searchInputPlaceholder="Search Schedulers..."
                                    />

                                </div>

                                <div className="col-span-1 flex items-center mt-6">

                                    <label className="flex items-center gap-3 cursor-pointer">

                                        <div className="relative">

                                            <input

                                                type="checkbox"

                                                className="sr-only"

                                                checked={isEnabled}

                                                onChange={(e) => setIsEnabled(e.target.checked)}

                                            />

                                            <div className={`block w-10 h-6 rounded-full transition-colors ${isEnabled ? 'bg-green-500' : 'bg-slate-700'}`}></div>

                                            <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${isEnabled ? 'transform translate-x-4' : ''}`}></div>

                                        </div>

                                        <span className="text-sm font-semibold text-slate-300">{isEnabled ? 'Enabled' : 'Disabled'}</span>

                                    </label>

                                </div>

                                {!level.id && (

                                    <div className="col-span-2 mt-2">

                                        <MultiSelectApiDropdown

                                            label="Clients"

                                            required

                                            value={clients}

                                            onChange={setClients}

                                            apiEndpoint="/clients"

                                            displayProperty="name"

                                            idProperty="id"

                                            searchFields={['name']}

                                            placeholder="Select Clients"

                                        />

                                    </div>

                                )}

                            </div>

                        </div>



                        {/* 2. Task Parameters */}

                        {taskType && (

                            <div className="space-y-4 animate-in fade-in duration-300">

                                <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-widest border-b border-slate-800 pb-2 flex items-center gap-2">

                                    <Activity size={14} /> Task Parameters

                                </h3>

                                {isCustomReport ? (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2 col-span-2 sm:col-span-1">
                                            <label className="text-xs font-semibold text-slate-300">Format <span className="text-red-400">*</span></label>
                                            <CustomDropdown
                                                data={[
                                                    { label: 'Excel', value: 'excel' },
                                                    { label: 'Word (DOCX)', value: 'docx' },
                                                    { label: 'PDF', value: 'pdf' }
                                                ]}
                                                selectedValue={customReportFormat}
                                                onSelect={setCustomReportFormat}
                                                width="w-full"
                                                buttonHeight={11}
                                            />
                                        </div>

                                        <div className="col-span-2">
                                            <MultiSelectApiDropdown
                                                label="Custom Reports"
                                                required
                                                value={customReports}
                                                onChange={setCustomReports}
                                                apiEndpoint="/custom_reports"
                                                displayProperty="report_name"
                                                secondaryDisplayProperty="description"
                                                idProperty="id"
                                                searchFields={['report_name', 'name', 'description']}
                                                additionalQueries={level.id ? { client_id: level.id } : {}}
                                                placeholder="Select Custom Reports to Include"
                                                getDisplayText={(item) => item.report_name || item.name || item.title || `Report #${item.id}`}
                                            />
                                        </div>

                                        <div className="space-y-1.5 col-span-2">
                                            <label className="text-xs font-semibold text-slate-300">Recipients <span className="text-red-400">*</span></label>
                                            <div className="w-full min-h-11 bg-gray-900 border border-slate-700/50 rounded-lg p-1.5 flex flex-wrap gap-1.5 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/50 transition-all">
                                                {recipients.split(',').map(r => r.trim()).filter(r => r).map((email, index) => (
                                                    <span key={index} className="flex items-center gap-1 bg-slate-800 text-slate-200 px-2 py-1 rounded-md text-xs font-medium border border-slate-700">
                                                        {email}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const list = recipients.split(',').map(r => r.trim()).filter(r => r);
                                                                list.splice(index, 1);
                                                                setRecipients(list.join(', '));
                                                            }}
                                                            className="text-slate-400 hover:text-red-400 transition-colors"
                                                        >
                                                            <X size={12} />
                                                        </button>
                                                    </span>
                                                ))}
                                                <input
                                                    type="text"
                                                    value={recipientInput}
                                                    onChange={(e) => setRecipientInput(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ',') {
                                                            e.preventDefault();
                                                            const newEmail = recipientInput.trim().replace(',', '');
                                                            if (newEmail) {
                                                                if (!isValidEmail(newEmail)) {
                                                                    showError(`Invalid recipient email: ${newEmail}`);
                                                                    return;
                                                                }
                                                                const current = recipients.split(',').map(r => r.trim()).filter(r => r);
                                                                if (!current.includes(newEmail)) {
                                                                    setRecipients([...current, newEmail].join(', '));
                                                                }
                                                                setRecipientInput('');
                                                            }
                                                        } else if (e.key === 'Backspace' && !recipientInput && recipients) {
                                                            const list = recipients.split(',').map(r => r.trim()).filter(r => r);
                                                            list.pop();
                                                            setRecipients(list.join(', '));
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        const newEmail = recipientInput.trim().replace(',', '');
                                                        if (newEmail) {
                                                            if (!isValidEmail(newEmail)) {
                                                                showError(`Invalid recipient email: ${newEmail}`);
                                                                return;
                                                            }
                                                            const current = recipients.split(',').map(r => r.trim()).filter(r => r);
                                                            if (!current.includes(newEmail)) {
                                                                setRecipients([...current, newEmail].join(', '));
                                                            }
                                                            setRecipientInput('');
                                                        }
                                                    }}
                                                    placeholder={recipients ? "" : "Type email and press Enter"}
                                                    className="flex-1 min-w-[150px] bg-transparent border-none px-1 py-1 text-sm text-white focus:outline-none focus:ring-0 placeholder-slate-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : taskType.type === 'report_generation' ? (

                                    <div className="grid grid-cols-2 gap-4">

                                        <div className="space-y-2">

                                            <label className="text-xs font-semibold text-slate-300">Format <span className="text-red-400">*</span></label>

                                            <CustomDropdown

                                                data={[

                                                    { label: 'PDF', value: 'pdf' },

                                                    { label: 'CSV', value: 'csv' },

                                                    { label: 'Excel (XLSX)', value: 'xlsx' }

                                                ]}

                                                selectedValue={format}

                                                onSelect={setFormat}

                                                width="w-full"

                                                buttonHeight={11}

                                            />

                                        </div>

                                        <div className="space-y-2">

                                            <label className="text-xs font-semibold text-slate-300">Date Period <span className="text-red-400">*</span></label>

                                            <CustomDropdown

                                                data={[

                                                    { label: 'Last 24 Hours', value: 'last_24_hours' },

                                                    { label: 'Last 7 Days', value: 'last_7_days' },

                                                    { label: 'Last 30 Days', value: 'last_30_days' },

                                                    { label: 'This Month', value: 'this_month' }

                                                ]}

                                                selectedValue={datePeriod}

                                                onSelect={setDatePeriod}

                                                width="w-full"

                                                buttonHeight={11}

                                            />

                                        </div>

                                        <div className="col-span-2">

                                            <MultiSelectApiDropdown

                                                label="Reports"

                                                required

                                                value={reportKeys}

                                                onChange={setReportKeys}

                                                apiEndpoint="/reports/available"

                                                displayProperty="title"

                                                secondaryDisplayProperty="description"

                                                idProperty="report_key"

                                                searchFields={['title', 'description']}

                                                placeholder="Select Reports to Include"

                                            />

                                        </div>

                                        <div className="space-y-1.5 col-span-2">

                                            <label className="text-xs font-semibold text-slate-300">Recipients <span className="text-red-400">*</span></label>

                                            <div className="w-full min-h-11 bg-gray-900 border border-slate-700/50 rounded-lg p-1.5 flex flex-wrap gap-1.5 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/50 transition-all">

                                                {recipients.split(',').map(r => r.trim()).filter(r => r).map((email, index) => (

                                                    <span key={index} className="flex items-center gap-1 bg-slate-800 text-slate-200 px-2 py-1 rounded-md text-xs font-medium border border-slate-700">

                                                        {email}

                                                        <button

                                                            type="button"

                                                            onClick={() => {
                                                                const list = recipients.split(',').map(r => r.trim()).filter(r => r);
                                                                list.splice(index, 1);
                                                                setRecipients(list.join(', '));
                                                            }}

                                                            className="text-slate-400 hover:text-red-400 transition-colors"

                                                        >

                                                            <X size={12} />

                                                        </button>

                                                    </span>

                                                ))}

                                                <input

                                                    type="text"

                                                    value={recipientInput}

                                                    onChange={(e) => setRecipientInput(e.target.value)}

                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ',') {
                                                            e.preventDefault();
                                                            const newEmail = recipientInput.trim().replace(',', '');
                                                            if (newEmail) {
                                                                if (!isValidEmail(newEmail)) {
                                                                    showError(`Invalid recipient email: ${newEmail}`);
                                                                    return;
                                                                }
                                                                const current = recipients.split(',').map(r => r.trim()).filter(r => r);
                                                                if (!current.includes(newEmail)) {
                                                                    setRecipients([...current, newEmail].join(', '));
                                                                }
                                                                setRecipientInput('');
                                                            }
                                                        } else if (e.key === 'Backspace' && !recipientInput && recipients) {
                                                            const list = recipients.split(',').map(r => r.trim()).filter(r => r);
                                                            list.pop();
                                                            setRecipients(list.join(', '));
                                                        }
                                                    }}

                                                    onBlur={() => {
                                                        const newEmail = recipientInput.trim().replace(',', '');
                                                        if (newEmail) {
                                                            if (!isValidEmail(newEmail)) {
                                                                showError(`Invalid recipient email: ${newEmail}`);
                                                                return;
                                                            }
                                                            const current = recipients.split(',').map(r => r.trim()).filter(r => r);
                                                            if (!current.includes(newEmail)) {
                                                                setRecipients([...current, newEmail].join(', '));
                                                            }
                                                            setRecipientInput('');
                                                        }
                                                    }}

                                                    placeholder={recipients ? "" : "Type email and press Enter"}

                                                    className="flex-1 min-w-[150px] bg-transparent border-none px-1 py-1 text-sm text-white focus:outline-none focus:ring-0 placeholder-slate-500"

                                                />

                                            </div>

                                        </div>

                                    </div>

                                ) : taskType.type === 'custom-script-execution' ? (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2">
                                            <MultiSelectApiDropdown
                                                label="Select Scripts"
                                                required
                                                value={selectedScripts}
                                                onChange={setSelectedScripts}
                                                apiEndpoint="/custom_scripts"
                                                displayProperty="name"
                                                idProperty="id"
                                                searchFields={['name']}
                                                placeholder="Select Scripts"
                                                additionalQueries={level.id ? { client_id: level.id } : {}}
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <MultiSelectApiDropdown
                                                label="Agents"
                                                required
                                                value={agents}
                                                onChange={setAgents}
                                                apiEndpoint="/agents"
                                                displayProperty="name"
                                                idProperty="id"
                                                searchFields={['name', 'ip_address']}
                                                placeholder="Select Agents"
                                                additionalQueries={getAgentsAdditionalQueries()}
                                            />
                                        </div>
                                    </div>
                                ) : (

                                    <div className="grid grid-cols-2 gap-4">

                                        <div className="col-span-2">

                                            <MultiSelectApiDropdown

                                                label="Agents"

                                                required

                                                value={agents}

                                                onChange={setAgents}

                                                apiEndpoint="/agents"

                                                displayProperty="name"

                                                idProperty="id"

                                                searchFields={['name', 'ip_address']}

                                                placeholder="Select Agents"

                                                additionalQueries={getAgentsAdditionalQueries()}

                                            />

                                        </div>

                                    </div>

                                )}

                            </div>

                        )}



                        {/* 3. Timing Configuration */}

                        <div className="space-y-4">

                            <h3 className="text-sm font-bold text-orange-400 uppercase tracking-widest border-b border-slate-800 pb-2 flex items-center gap-2">

                                <Clock size={14} /> Timing Configuration

                            </h3>

                            <div className="grid grid-cols-2 gap-4 items-start">

                                {showTimezone && (
                                    <div className="col-span-2">

                                        <div className="space-y-1.5">

                                            <div className="flex items-start gap-2 bg-slate-800/40 border border-slate-700/30 rounded-lg p-2.5 mb-1.5">
                                                <Info size={14} className="text-indigo-400 mt-0.5 shrink-0" />
                                                <p className="text-xs text-slate-400 leading-relaxed">
                                                    Note: If no timezone is selected, the schedule will run in the <strong>UTC</strong> timezone by default.
                                                </p>
                                            </div>

                                            <label className="text-xs font-semibold text-slate-300">Timezone <span className="text-red-400">*</span></label>

                                            <SingleSelectLocalSearchDropdown

                                                options={timezoneList}

                                                value={timezone}

                                                onChange={setTimezone}

                                                placeholder="Select Timezone"

                                                searchInputPlaceholder="Search timezones..."

                                            />

                                        </div>

                                    </div>
                                )}

                                <div className="space-y-2 col-span-2 sm:col-span-1">

                                    <label className="text-xs font-semibold text-slate-300">Frequency <span className="text-red-400">*</span></label>

                                    <CustomDropdown

                                        data={frequencyOptions}

                                        selectedValue={schedulerType}

                                        onSelect={setSchedulerType}

                                        width="w-full"

                                        buttonHeight={11}

                                    />

                                </div>



                                <div className="col-span-2 sm:col-span-1 grid grid-cols-2 gap-2">

                                    <div className="space-y-1.5">

                                        <label className="text-xs font-semibold text-slate-300">

                                            Hour <span className="text-red-400">*</span>

                                        </label>

                                        <CustomDropdown

                                            data={availableHourOptions}

                                            selectedValue={hours}

                                            onSelect={setHours}

                                            width="w-full"

                                            buttonHeight={11}

                                            placeholder="Select Hour"

                                        />

                                    </div>

                                    <div className="space-y-1.5">

                                        <label className="text-xs font-semibold text-slate-300">

                                            Minute <span className="text-red-400">*</span>

                                        </label>

                                        <CustomDropdown

                                            data={availableMinuteOptions}

                                            selectedValue={minutes}

                                            onSelect={setMinutes}

                                            width="w-full"

                                            buttonHeight={11}

                                            placeholder="Select Minute"

                                        />

                                    </div>

                                </div>







                                {/* Conditional Timing Fields */}

                                {schedulerType === 'weekly' && (

                                    <div className="space-y-2 col-span-2 mt-2">

                                        <label className="text-xs font-semibold text-slate-300 block">Days of Week <span className="text-red-400">*</span></label>

                                        <div className="flex flex-wrap gap-2">

                                            {daysList.map(day => (

                                                <button

                                                    type="button"

                                                    key={day}

                                                    onClick={() => {

                                                        if (daysOfWeek.includes(day)) {

                                                            setDaysOfWeek(daysOfWeek.filter(d => d !== day));

                                                        } else {

                                                            setDaysOfWeek([...daysOfWeek, day]);

                                                        }

                                                    }}

                                                    className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-colors ${daysOfWeek.includes(day)

                                                        ? 'bg-orange-500 border-orange-500 text-white'

                                                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'

                                                        }`}

                                                >

                                                    {day.charAt(0).toUpperCase() + day.slice(1)}

                                                </button>

                                            ))}

                                        </div>

                                    </div>

                                )}



                                {(schedulerType === 'monthly' || schedulerType === 'quarterly' || schedulerType === 'yearly') && (

                                    <div className="space-y-2 col-span-2 mt-2">

                                        <div className="flex items-center justify-between">

                                            <label className="text-xs font-semibold text-slate-300">Days of Month (Multiple) <span className="text-red-400">*</span></label>

                                            {selectedDays.length === 0 && <span className="text-xs text-red-400">Select at least one day</span>}

                                        </div>

                                        <div className="flex flex-wrap gap-1.5 bg-gray-900 border border-slate-700/50 p-2 rounded-lg max-h-40 overflow-y-auto custom-scroll">

                                            {monthDaysList.map(day => (

                                                <button

                                                    type="button"

                                                    key={day}

                                                    onClick={() => toggleMonthDay(day)}

                                                    className={`w-8 h-8 flex items-center justify-center text-xs font-bold rounded-md border transition-all ${selectedDays.includes(day)

                                                        ? 'bg-orange-500 border-orange-500 text-white shadow-md shadow-orange-500/20'

                                                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-white'

                                                        }`}

                                                >

                                                    {day}

                                                </button>

                                            ))}

                                        </div>

                                    </div>

                                )}



                                {schedulerType === 'yearly' && (

                                    <div className="space-y-2 col-span-2 mt-2">

                                        <div className="flex items-center justify-between">

                                            <label className="text-xs font-semibold text-slate-300">Months (Multiple) <span className="text-red-400">*</span></label>

                                            {selectedMonths.length === 0 && <span className="text-xs text-red-400">Select at least one month</span>}

                                        </div>

                                        <div className="flex flex-wrap gap-2 bg-gray-900 border border-slate-700/50 p-2.5 rounded-lg">

                                            {monthsList.map(month => (

                                                <button

                                                    type="button"

                                                    key={month}

                                                    onClick={() => toggleMonth(month)}


                                                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all flex-grow sm:flex-grow-0 text-center ${selectedMonths.includes(month)

                                                        ? 'bg-orange-500 border-orange-500 text-white shadow-md shadow-orange-500/20'

                                                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700 hover:text-white'

                                                        }`}

                                                >

                                                    {month.charAt(0).toUpperCase() + month.slice(1, 3)}

                                                </button>

                                            ))}

                                        </div>

                                    </div>

                                )}



                                {schedulerType === 'one_time' && (

                                    <div className="space-y-1.5 col-span-2 mt-2">

                                        <label className="text-xs font-semibold text-slate-300">Date (YYYY-MM-DD) <span className="text-red-400">*</span></label>

                                        <input

                                            type="date"

                                            value={oneTimeDate}

                                            onChange={(e) => setOneTimeDate(e.target.value)}

                                            className="w-full h-11 bg-gray-900 border border-slate-700/50 rounded-lg px-3 py-2.5 text-sm text-white [color-scheme:dark] focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all"

                                        />

                                    </div>

                                )}

                            </div>

                        </div>

                    </div>

                </div>



                {/* Footer */}

                <div className="px-6 py-4 border-t border-slate-800 bg-slate-800/50 flex justify-end gap-3 shrink-0 rounded-b-xl">

                    <button

                        type="button"

                        onClick={onClose}

                        className="px-4 py-2 text-sm font-semibold text-slate-300 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 transition-colors"

                    >

                        Cancel

                    </button>

                    <button

                        onClick={handleSubmit}

                        disabled={loading}

                        className="flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-orange-600 rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"

                    >

                        {loading ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" /> : <Save size={16} />}

                        {editData ? 'Update Schedule' : 'Save Schedule'}

                    </button>

                </div>

            </div>

        </div>

    );

}
